/**
 * Form Submission Service
 * Handles background saves with retry logic for form data
 */

import { supabase } from '../lib/supabase';
import { logger } from '../lib/utils';

interface SaveQueueItem {
  session_id: string;
  step: string;
  data: Record<string, any>;
  completed?: boolean;
  retries: number;
  timestamp: number;
}

// Configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds
const MAX_RETRY_DELAY = 10000; // 10 seconds

// In-memory queue for failed saves
let saveQueue: SaveQueueItem[] = [];
let isProcessingQueue = false;

/**
 * Get Supabase Edge Function URL
 */
const getEdgeFunctionUrl = (functionName: string): string => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not defined');
  }
  return `${supabaseUrl}/functions/v1/${functionName}`;
};

/**
 * Save form step data (main function)
 * Saves data and adds to retry queue on failure
 */
export const saveFormStep = async (
  sessionId: string,
  step: string,
  data: Record<string, any>,
  completed: boolean = false
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get anon key for authorization
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!anonKey) {
      throw new Error('VITE_SUPABASE_ANON_KEY is not defined');
    }

    const response = await fetch(getEdgeFunctionUrl('save-form-step'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
      },
      body: JSON.stringify({
        session_id: sessionId,
        step,
        data,
        completed,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to save form step');
    }

    logger.log(`Form step '${step}' saved successfully`);
    return { success: true };

  } catch (error) {
    logger.error(`Error saving form step '${step}':`, error);
    
    // Add to retry queue
    addToRetryQueue(sessionId, step, data, completed);
    
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

/**
 * Add failed save to retry queue
 */
const addToRetryQueue = (
  sessionId: string,
  step: string,
  data: Record<string, any>,
  completed: boolean = false
): void => {
  // Check if item already in queue
  const existingIndex = saveQueue.findIndex(
    item => item.session_id === sessionId && item.step === step
  );

  if (existingIndex >= 0) {
    // Update existing item
    saveQueue[existingIndex] = {
      session_id: sessionId,
      step,
      data,
      completed,
      retries: saveQueue[existingIndex].retries,
      timestamp: Date.now(),
    };
  } else {
    // Add new item
    saveQueue.push({
      session_id: sessionId,
      step,
      data,
      completed,
      retries: 0,
      timestamp: Date.now(),
    });
  }

  logger.log(`Added form step '${step}' to retry queue`);
  
  // Start processing queue if not already running
  if (!isProcessingQueue) {
    processRetryQueue();
  }
};

/**
 * Process retry queue with exponential backoff
 */
const processRetryQueue = async (): Promise<void> => {
  if (isProcessingQueue || saveQueue.length === 0) {
    return;
  }

  isProcessingQueue = true;

  while (saveQueue.length > 0) {
    const item = saveQueue[0];

    // Check if max retries exceeded
    if (item.retries >= MAX_RETRIES) {
      logger.error(`Max retries exceeded for step '${item.step}', removing from queue`);
      saveQueue.shift();
      continue;
    }

    // Calculate delay with exponential backoff
    const delay = Math.min(
      RETRY_DELAY * Math.pow(2, item.retries),
      MAX_RETRY_DELAY
    );

    // Wait before retry
    await new Promise(resolve => setTimeout(resolve, delay));

    logger.log(`Retrying save for step '${item.step}' (attempt ${item.retries + 1}/${MAX_RETRIES})`);

    try {
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (!anonKey) {
        throw new Error('VITE_SUPABASE_ANON_KEY is not defined');
      }

      const response = await fetch(getEdgeFunctionUrl('save-form-step'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          session_id: item.session_id,
          step: item.step,
          data: item.data,
          completed: item.completed,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        logger.log(`Successfully saved step '${item.step}' on retry`);
        saveQueue.shift(); // Remove from queue
      } else {
        throw new Error(result.error || 'Retry failed');
      }

    } catch (error) {
      logger.error(`Retry failed for step '${item.step}':`, error);
      item.retries++;
      
      if (item.retries >= MAX_RETRIES) {
        saveQueue.shift(); // Remove from queue
        logger.error(`Giving up on step '${item.step}' after ${MAX_RETRIES} retries`);
      }
    }
  }

  isProcessingQueue = false;
};

/**
 * Get complete session data
 */
export const getSessionData = async (sessionId: string): Promise<{
  success: boolean;
  data?: {
    session: any;
    form_steps: any[];
    lead: any | null;
  };
  error?: string;
}> => {
  try {
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!anonKey) {
      throw new Error('VITE_SUPABASE_ANON_KEY is not defined');
    }

    const response = await fetch(
      `${getEdgeFunctionUrl('get-session-data')}?session_id=${sessionId}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${anonKey}`,
        },
      }
    );

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to get session data');
    }

    return {
      success: true,
      data: {
        session: result.session,
        form_steps: result.form_steps,
        lead: result.lead,
      },
    };

  } catch (error) {
    logger.error('Error getting session data:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Get current retry queue status (for debugging)
 */
export const getRetryQueueStatus = (): {
  queueLength: number;
  isProcessing: boolean;
  items: Array<{ step: string; retries: number }>;
} => {
  return {
    queueLength: saveQueue.length,
    isProcessing: isProcessingQueue,
    items: saveQueue.map(item => ({
      step: item.step,
      retries: item.retries,
    })),
  };
};

/**
 * Manually trigger retry queue processing (for debugging)
 */
export const triggerRetryQueue = (): void => {
  if (!isProcessingQueue && saveQueue.length > 0) {
    processRetryQueue();
  }
};

